// =============================================================
// Widget templates — one file per kind. Each exports a function
// that takes a config object and returns HTML string.
//
// All templates use the same outer structure:
//   <section class="widget widget--{kind}" data-widget="{kind}">
//     ...
//   </section>
// so widget-modifiers.ts can find them via [data-widget] selector
// and apply CSS classes + IntersectionObserver wiring.
// =============================================================

export function testimonialsTpl(config: any): string {
  const items: Array<{ quote: string; author: string; role?: string; avatarUrl?: string }> = config?.items ?? [];
  if (items.length === 0) {
    return `<section class="widget widget--testimonials" data-widget="testimonials"><p class="widget__empty">No testimonials yet.</p></section>`;
  }
  const cards = items.map((it) => `
    <article class="testimonial">
      <blockquote class="testimonial__quote">“${escapeHtml(it.quote)}”</blockquote>
      <footer class="testimonial__footer">
        ${it.avatarUrl ? `<img class="testimonial__avatar" src="${escapeAttr(it.avatarUrl)}" alt="${escapeAttr(it.author)}" />` : ''}
        <div>
          <div class="testimonial__author">${escapeHtml(it.author)}</div>
          ${it.role ? `<div class="testimonial__role">${escapeHtml(it.role)}</div>` : ''}
        </div>
      </footer>
    </article>`).join('');
  return `<section class="widget widget--testimonials" data-widget="testimonials"><div class="testimonials">${cards}</div></section>`;
}

export function pricingTpl(config: any): string {
  const tiers: Array<{ tier: string; price: number; period?: string; features?: string[]; ctaLabel?: string; ctaUrl?: string; highlighted?: boolean }> = config?.tiers ?? [];
  if (tiers.length === 0) return `<section class="widget widget--pricing" data-widget="pricing"><p class="widget__empty">No tiers configured.</p></section>`;
  const cards = tiers.map(t => `
    <article class="pricing-tier${t.highlighted ? ' is-highlighted' : ''}">
      <h3 class="pricing-tier__name">${escapeHtml(t.tier)}</h3>
      <div class="pricing-tier__price"><span class="pricing-tier__amount">${formatPrice(t.price)}</span><span class="pricing-tier__period">/${escapeHtml(t.period || 'mo')}</span></div>
      <ul class="pricing-tier__features">
        ${(t.features ?? []).map(f => `<li>${escapeHtml(f)}</li>`).join('')}
      </ul>
      ${t.ctaLabel ? `<a class="btn btn--${t.highlighted ? 'primary' : 'ghost'}" href="${escapeAttr(t.ctaUrl || '#')}">${escapeHtml(t.ctaLabel)}</a>` : ''}
    </article>`).join('');
  return `<section class="widget widget--pricing" data-widget="pricing"><div class="pricing-grid">${cards}</div></section>`;
}

export function servicesTpl(config: any): string {
  const items: Array<{ icon?: string; title: string; description?: string; priceFrom?: number }> = config?.items ?? [];
  if (items.length === 0) return `<section class="widget widget--services" data-widget="services"><p class="widget__empty">No services.</p></section>`;
  const cards = items.map(s => `
    <article class="service-card">
      <div class="service-card__icon">${getIcon(s.icon || 'star')}</div>
      <h3 class="service-card__title">${escapeHtml(s.title)}</h3>
      ${s.description ? `<p class="service-card__desc">${escapeHtml(s.description)}</p>` : ''}
      ${s.priceFrom ? `<div class="service-card__price">from ${formatPrice(s.priceFrom)}</div>` : ''}
    </article>`).join('');
  return `<section class="widget widget--services" data-widget="services"><div class="services-grid">${cards}</div></section>`;
}

export function teamTpl(config: any): string {
  const items: Array<{ name: string; role: string; bio?: string; photoUrl?: string; social?: Array<{ platform: string; url: string }> }> = config?.items ?? [];
  if (items.length === 0) return `<section class="widget widget--team" data-widget="team"><p class="widget__empty">No team members.</p></section>`;
  const cards = items.map(m => `
    <article class="team-card">
      ${m.photoUrl ? `<img class="team-card__photo" src="${escapeAttr(m.photoUrl)}" alt="${escapeAttr(m.name)}" />` : `<div class="team-card__placeholder">${escapeHtml(m.name.charAt(0).toUpperCase())}</div>`}
      <h3 class="team-card__name">${escapeHtml(m.name)}</h3>
      <div class="team-card__role">${escapeHtml(m.role)}</div>
      ${m.bio ? `<p class="team-card__bio">${escapeHtml(m.bio)}</p>` : ''}
      ${m.social && m.social.length ? `<div class="team-card__social">${m.social.map(s => `<a href="${escapeAttr(s.url)}" rel="noopener" aria-label="${escapeAttr(s.platform)}">${getIcon(s.platform)}</a>`).join('')}</div>` : ''}
    </article>`).join('');
  return `<section class="widget widget--team" data-widget="team"><div class="team-grid">${cards}</div></section>`;
}

export function galleryTpl(config: any): string {
  const urls: string[] = config?.imageUrls ?? [];
  const columns: number = config?.columns ?? 3;
  if (urls.length === 0) return `<section class="widget widget--gallery" data-widget="gallery"><p class="widget__empty">No images.</p></section>`;
  const imgs = urls.map(u => `<a class="gallery__item" href="${escapeAttr(u)}" target="_blank" rel="noopener"><img src="${escapeAttr(u)}" loading="lazy" alt="" /></a>`).join('');
  return `<section class="widget widget--gallery" data-widget="gallery"><div class="gallery" style="--gallery-cols:${columns}">${imgs}</div></section>`;
}

export function faqTpl(config: any): string {
  const items: Array<{ question: string; answer: string }> = config?.items ?? [];
  if (items.length === 0) return `<section class="widget widget--faq" data-widget="faq"><p class="widget__empty">No FAQs.</p></section>`;
  const faqs = items.map((it) => `
    <details class="faq-item">
      <summary class="faq-item__q">${escapeHtml(it.question)}</summary>
      <div class="faq-item__a">${escapeHtml(it.answer)}</div>
    </details>`).join('');
  return `<section class="widget widget--faq" data-widget="faq"><div class="faq">${faqs}</div></section>`;
}

export function statsTpl(config: any): string {
  const items: Array<{ label: string; value: string | number; prefix?: string; suffix?: string }> = config?.items ?? [];
  if (items.length === 0) return `<section class="widget widget--stats" data-widget="stats"></section>`;
  const cells = items.map(it => `
    <div class="stat">
      <div class="stat__value">${escapeHtml(it.prefix || '')}<span class="stat__number">${escapeHtml(String(it.value))}</span>${escapeHtml(it.suffix || '')}</div>
      <div class="stat__label">${escapeHtml(it.label)}</div>
    </div>`).join('');
  return `<section class="widget widget--stats" data-widget="stats"><div class="stats">${cells}</div></section>`;
}

export function openingHoursTpl(_config: any): string {
  // hours is read from window.__SLOTH_BUSINESS__ (injected by main.ts).
  // The widget shell is empty until hydration.
  return `<section class="widget widget--opening-hours" data-widget="opening-hours" data-bind="opening-hours"></section>`;
}

export function quickLoginTpl(config: any): string {
  const title = config?.title || 'Member sign-in';
  const ctaLabel = config?.ctaLabel || 'Sign in';
  return `
    <section class="widget widget--quick-login" data-widget="quick-login">
      <form class="quick-login" onsubmit="return false">
        <h3 class="quick-login__title">${escapeHtml(title)}</h3>
        <div class="quick-login__row">
          <label>Email <input type="email" name="email" required autocomplete="email" /></label>
          <label>Password <input type="password" name="password" required autocomplete="current-password" /></label>
        </div>
        <button type="submit" class="btn btn--primary">${escapeHtml(ctaLabel)}</button>
        <div class="quick-login__error" hidden></div>
        <div class="quick-login__success" hidden></div>
      </form>
    </section>`;
}

export function registerTpl(config: any): string {
  const title = config?.title || 'Create an account';
  const ctaLabel = config?.ctaLabel || 'Register';
  const requirePhone = !!config?.requirePhone;
  return `
    <section class="widget widget--register" data-widget="register">
      <form class="register-form" onsubmit="return false">
        <h3 class="register-form__title">${escapeHtml(title)}</h3>
        <div class="register-form__grid">
          <label>First name <input type="text" name="firstName" required /></label>
          <label>Last name <input type="text" name="lastName" required /></label>
          <label>Email <input type="email" name="email" required autocomplete="email" /></label>
          ${requirePhone ? '<label>Phone <input type="tel" name="phone" autocomplete="tel" /></label>' : ''}
          <label>Password <input type="password" name="password" required minlength="8" autocomplete="new-password" /></label>
        </div>
        <button type="submit" class="btn btn--primary">${escapeHtml(ctaLabel)}</button>
        <div class="register-form__error" hidden></div>
        <div class="register-form__success" hidden></div>
      </form>
    </section>`;
}

export function payTpl(config: any): string {
  const provider = config?.provider || 'manual';
  const amount = Number(config?.amount || 0);
  const currency = config?.currency || 'EUR';
  const description = config?.description || 'Service payment';
  const buttonLabel = config?.buttonLabel || 'Pay now';
  const returnUrl = config?.returnUrl || '/';
  return `
    <section class="widget widget--pay" data-widget="pay">
      <div class="pay-button">
        <div class="pay-button__meta">
          <div class="pay-button__desc">${escapeHtml(description)}</div>
          <div class="pay-button__amount">${formatPrice(amount)} ${escapeHtml(currency)}</div>
        </div>
        <button class="btn btn--primary pay-button__btn" data-provider="${escapeAttr(provider)}" data-amount="${amount}" data-currency="${escapeAttr(currency)}" data-return="${escapeAttr(returnUrl)}" type="button">
          ${escapeHtml(buttonLabel)}
        </button>
        <div class="pay-button__provider">via ${escapeHtml(provider.toUpperCase())}</div>
      </div>
    </section>`;
}

export function socialTpl(config: any): string {
  const items: Array<{ platform: string; url: string; label?: string }> = config?.items ?? [];
  if (items.length === 0) return `<section class="widget widget--social" data-widget="social"></section>`;
  const links = items.map(s => `<a class="social-link" href="${escapeAttr(s.url)}" target="_blank" rel="noopener" aria-label="${escapeAttr(s.platform)}">${getIcon(s.platform)}<span>${escapeHtml(s.label || s.platform)}</span></a>`).join('');
  return `<section class="widget widget--social" data-widget="social"><div class="social-links">${links}</div></section>`;
}

// ----- helpers -----
function escapeHtml(s: string): string {
  return String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] || c);
}
function escapeAttr(s: string): string { return escapeHtml(s); }
function formatPrice(n: number): string {
  if (n >= 1000) return n.toLocaleString('en-US');
  return String(n);
}

const ICONS: Record<string, string> = {
  star: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon></svg>',
  users: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>',
  heart: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/></svg>',
  facebook: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"/></svg>',
  instagram: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>',
  tiktok: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005.8 20.1a6.34 6.34 0 0010.86-4.43V8.84a8.16 8.16 0 004.77 1.52V6.91a4.85 4.85 0 01-1.84-.22z"/></svg>',
  youtube: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>',
  twitter: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/></svg>',
  linkedin: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.063 2.063 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>',
  whatsapp: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 018.413 3.488 11.824 11.824 0 013.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 001.683 5.452l-.999 3.648 3.805-.799zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/></svg>',
};
function getIcon(name: string): string {
  return ICONS[name] || ICONS.star;
}
